import { Checkbox, Form, Input, Select, Button, message } from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { capitalizeFirstLetter } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { CONSENT_FIELDS, CONSENT_OPTIONS } from "components/consentTag";
import { useUserType } from "features/auth";
import { usePostDebtorCommunicationHistoryMutation } from "features/communicationsTable/agencyPortal/communicationsTableAPI";
import { usePostCreditorPortalDebtorCommunicationHistoryMutation } from "features/communicationsTable/creditorPortal/communicationsTableAPI";
import { useFetchDebtorContactsQuery } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchCreditorPortalDebtorContactsQuery } from "features/debtorSider/creditorPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const DEBTOR_CONTACT_VALUE = "debtor_contact";

const StyledComposer = styled.div`
  border: 1px solid ${({ $expanded }) => ($expanded ? "#1677ff" : "#d9d9d9")};
  border-radius: 6px;
  background: #fff;
  margin-bottom: 12px;
  transition: border-color 0.2s;
`;

const StyledCollapsedInner = styled.div`
  display: flex;
  align-items: stretch;
`;

const StyledModeAddon = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid #d9d9d9;
  padding: 0 4px;
`;

const StyledSaveAddon = styled.div`
  display: flex;
  border-left: 1px solid #d9d9d9;
`;

const StyledSaveButton = styled(Button)`
  height: 100%;
  border-radius: 0 5px 5px 0;
  border: none;
`;

const StyledModeSelect = styled(Select)`
  width: 142px;

  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
`;

const StyledMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 12px;
  padding: 8px 12px 0;
`;

const StyledConsentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 12px;
  padding: 0 12px;
`;

const StyledNoteArea = styled.div`
  padding: 4px 12px 0;
`;

const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 12px 8px;
  border-top: 1px solid #f0f0f0;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 4px;
`;

const StyledAdvancedViewButton = styled(Button)`
  padding: 0;
  height: auto;
`;

const StyledHideAdvancedViewButton = styled(Button)`
  margin-right: auto;
  padding: 0;
`;

const QUICK_NOTE_CONTACT_METHOD = "note";
const QUICK_NOTE_CONTACT_TYPE = "debtor";

const MODE_OPTIONS = [
  { value: "communication", label: "Communication" },
  // { value: "quick_note", label: "Quick Note" },
];

function InlineComposer({ debtorId, onSuccess, mode = "communication", showModeSelect = false }) {
  const [selectedMode, setSelectedMode] = useState(mode);
  const isQuickNoteMode = selectedMode === "quick_note";

  useEffect(() => {
    setSelectedMode(mode);
  }, [mode]);
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState("");
  const collapsedInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [form] = Form.useForm();

  const { data: agencyDebtorContacts } = useFetchDebtorContactsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorContacts } = useFetchCreditorPortalDebtorContactsQuery(
    { debtorId },
    { skip: !isCreditorUserType },
  );
  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorDebtorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    { skip: !isCreditorUserType },
  );

  const debtorContacts = isAgencyUserType ? agencyDebtorContacts : creditorPortalDebtorContacts;
  const debtorAccounts = isAgencyUserType ? agencyDebtorAccounts : creditorDebtorAccounts;

  const [addAgencyCommunication, { isLoading: isAgencyLoading }] =
    usePostDebtorCommunicationHistoryMutation();
  const [addCreditorCommunication, { isLoading: isCreditorLoading }] =
    usePostCreditorPortalDebtorCommunicationHistoryMutation();
  const [addCommunicationAction, isLoading] = isAgencyUserType
    ? [addAgencyCommunication, isAgencyLoading]
    : [addCreditorCommunication, isCreditorLoading];

  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery({ isEnabled: true });

  const callResultItems = useMemo(
    () => allResultItems.filter((item) => item.resultType === "call"),
    [allResultItems],
  );
  const textResultItems = useMemo(
    () => allResultItems.filter((item) => item.resultType === "text"),
    [allResultItems],
  );
  const mailResultItems = useMemo(
    () => allResultItems.filter((item) => item.resultType === "mail"),
    [allResultItems],
  );
  const communicationResultItems = useMemo(
    () => allResultItems.filter((item) => item.resultType === "communication"),
    [allResultItems],
  );

  const filteredDebtorContacts = useMemo(
    () => debtorContacts?.filter((c) => !c.isArchived),
    [debtorContacts],
  );

  const extraRecipientOptions = useMemo(
    () =>
      (constants?.debtorCommunicationContactTypes ?? [])
        .filter(({ value }) => value !== DEBTOR_CONTACT_VALUE)
        .map(({ value, display }) => ({ value, label: display })),
    [constants],
  );

  const expand = useCallback(() => {
    setExpanded(true);
    form.setFieldsValue({
      contactMethod: form.getFieldValue("contactMethod") ?? "phone",
      communicationOrigin: form.getFieldValue("communicationOrigin") ?? "outbound",
      communicationStartTime:
        form.getFieldValue("communicationStartTime") ?? moment().format(DATE_TIME_FORMAT),
      recipient: form.getFieldValue("recipient") ?? filteredDebtorContacts?.[0]?.id,
      notes: noteText || form.getFieldValue("notes"),
    });
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [filteredDebtorContacts, form, noteText]);

  const collapse = useCallback(() => {
    setExpanded(false);
    setSelectedMode(mode);
    setNoteText("");
    form.resetFields();
  }, [form, mode]);

  const hideAdvancedView = useCallback(() => {
    setNoteText(form.getFieldValue("notes") ?? "");
    setExpanded(false);
    form.resetFields();
  }, [form]);

  const handleCollapsedSubmit = useCallback(async () => {
    if (!noteText?.trim()) return;
    let values;
    if (isQuickNoteMode) {
      values = {
        notes: noteText,
        contactMethod: QUICK_NOTE_CONTACT_METHOD,
        contactType: QUICK_NOTE_CONTACT_TYPE,
        communicationStartTime: moment().format(DATE_TIME_FORMAT),
      };
    } else {
      const firstContact = filteredDebtorContacts?.[0];
      values = {
        notes: noteText,
        contactMethod: "phone",
        communicationOrigin: "outbound",
        communicationStartTime: moment().format(DATE_TIME_FORMAT),
        debtorContactId: firstContact?.id ?? null,
        contactType: firstContact
          ? DEBTOR_CONTACT_VALUE
          : extraRecipientOptions?.[0]?.value ?? "debtor",
      };
    }
    const result = await addCommunicationAction({ communication: values, debtorId });
    if ("data" in result) {
      message.success(
        isQuickNoteMode ? "Note added successfully!" : "Communication log added successfully!",
      );
      setNoteText("");
      setTimeout(() => collapsedInputRef.current?.focus(), 50);
      onSuccess?.();
    }
  }, [
    noteText,
    isQuickNoteMode,
    filteredDebtorContacts,
    extraRecipientOptions,
    addCommunicationAction,
    debtorId,
    onSuccess,
  ]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.altKey && e.code === "KeyN") {
        e.preventDefault();
        expand();
      }
      if (e.code === "Escape") {
        collapse();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [expand, collapse]);

  const handleSubmit = async () => {
    let values;
    if (isQuickNoteMode) {
      const notes = form.getFieldValue("notes");
      if (!notes?.trim()) {
        form.setFields([{ name: "notes", errors: ["Please enter a note"] }]);
        return;
      }
      values = {
        notes,
        contactMethod: QUICK_NOTE_CONTACT_METHOD,
        contactType: QUICK_NOTE_CONTACT_TYPE,
        communicationStartTime: moment().format(DATE_TIME_FORMAT),
      };
    } else {
      try {
        values = await form.validateFields();
      } catch {
        return;
      }
    }
    const result = await addCommunicationAction({ communication: values, debtorId });
    if ("data" in result) {
      message.success(
        isQuickNoteMode ? "Note added successfully!" : "Communication log added successfully!",
      );
      form.resetFields();
      setNoteText("");
      form.setFieldsValue({
        contactMethod: "phone",
        communicationOrigin: "outbound",
        communicationStartTime: moment().format(DATE_TIME_FORMAT),
        recipient: filteredDebtorContacts?.[0]?.id,
      });
      setTimeout(() => textareaRef.current?.focus(), 50);
      onSuccess?.();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  const handleWrapperKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      collapse();
    }
  };

  if (!expanded) {
    return (
      <StyledComposer>
        <StyledCollapsedInner>
          {showModeSelect && (
            <StyledModeAddon>
              <StyledModeSelect
                value={selectedMode}
                options={MODE_OPTIONS}
                onChange={(val) => setSelectedMode(val)}
                popupMatchSelectWidth={false}
              />
            </StyledModeAddon>
          )}
          <Input
            ref={collapsedInputRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onPressEnter={handleCollapsedSubmit}
            placeholder={isQuickNoteMode ? "Type a quick note..." : "Type a note..."}
            variant="borderless"
            suffix={
              !isQuickNoteMode ? (
                <StyledAdvancedViewButton type="link" size="small" onClick={expand}>
                  Show advanced view
                </StyledAdvancedViewButton>
              ) : null
            }
          />
          <StyledSaveAddon>
            <StyledSaveButton type="primary" loading={isLoading} onClick={handleCollapsedSubmit}>
              Save
            </StyledSaveButton>
          </StyledSaveAddon>
        </StyledCollapsedInner>
      </StyledComposer>
    );
  }

  return (
    <StyledComposer $expanded onKeyDown={handleWrapperKeyDown}>
      <Form form={form} layout="vertical" size="small">
        <StyledMetaGrid>
          <StyledFormItem
            label="Contact Method"
            name="contactMethod"
            rules={[{ required: true, message: "Required" }]}
            initialValue="phone"
          >
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={constants?.contactMethods?.map((m) => ({
                label: m.display,
                value: m.value,
              }))}
            />
          </StyledFormItem>
          <StyledFormItem label="Origin" name="communicationOrigin">
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={constants?.communicationOrigin
                ?.filter((o) => Boolean(o.value))
                .map((o) => ({ label: o.display, value: o.value }))}
            />
          </StyledFormItem>
          <StyledFormItem
            label="Recipient"
            name="recipient"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              options={[
                ...(filteredDebtorContacts?.map((c) => ({
                  value: c.id,
                  label: [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" "),
                })) ?? []),
                ...extraRecipientOptions,
              ]}
            />
          </StyledFormItem>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.recipient !== cur.recipient}>
            {({ getFieldValue, setFieldValue }) => {
              const recipient = getFieldValue("recipient");
              if (recipient === undefined) return null;
              const isExtra = extraRecipientOptions.map((o) => o.value).includes(recipient);
              setFieldValue("debtorContactId", isExtra ? null : recipient);
              setFieldValue("contactType", isExtra ? recipient : DEBTOR_CONTACT_VALUE);
              return (
                <>
                  <Form.Item name="debtorContactId" hidden>
                    <Input hidden />
                  </Form.Item>
                  <Form.Item name="contactType" hidden>
                    <Input hidden />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.debtorContactId !== cur.debtorContactId ||
              prev.contactMethod !== cur.contactMethod
            }
          >
            {({ getFieldValue, setFieldValue }) => {
              const contactMethod = getFieldValue("contactMethod");
              if (!["phone", "text"].includes(contactMethod?.toLowerCase())) return null;
              const debtorContactId = getFieldValue("debtorContactId");
              const debtorContact = debtorContacts?.find((c) => c.id === debtorContactId);
              const options =
                debtorContact?.phones?.map((p) => ({
                  label: `${p.number} - ${capitalizeFirstLetter(p.type)}`,
                  value: p.number,
                })) ?? [];
              if (options.length === 1) setFieldValue("phoneNumber", options[0].value);
              else setFieldValue("phoneNumber", undefined);
              return (
                <StyledFormItem label="Phone" name="phoneNumber">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    allowClear
                    options={options}
                  />
                </StyledFormItem>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.contactMethod !== cur.contactMethod}>
            {({ getFieldValue }) => {
              const cm = getFieldValue("contactMethod")?.toLowerCase();
              if (cm === "phone" || cm === "ai_dialer") {
                return (
                  <StyledFormItem label="Call Results" name="callResults">
                    <Select
                      showSearch
                      optionFilterProp="label"
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      allowClear
                      options={callResultItems.map((i) => ({ label: i.name, value: i.slug }))}
                    />
                  </StyledFormItem>
                );
              }
              if (cm === "text") {
                return (
                  <StyledFormItem label="Text Results" name="textResults">
                    <Select
                      showSearch
                      optionFilterProp="label"
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      allowClear
                      options={textResultItems.map((i) => ({ label: i.name, value: i.slug }))}
                    />
                  </StyledFormItem>
                );
              }
              if (cm === "mail") {
                return (
                  <StyledFormItem label="Mail Results" name="mailResults">
                    <Select
                      showSearch
                      optionFilterProp="label"
                      popupMatchSelectWidth={false}
                      mode="multiple"
                      allowClear
                      options={mailResultItems.map((i) => ({ label: i.name, value: i.slug }))}
                    />
                  </StyledFormItem>
                );
              }
              return null;
            }}
          </Form.Item>
          <StyledFormItem label="Comm. Results" name="communicationResults">
            <Select
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              options={communicationResultItems.map((i) => ({ label: i.name, value: i.slug }))}
            />
          </StyledFormItem>
        </StyledMetaGrid>
        <StyledConsentGrid>
          {CONSENT_FIELDS.map((field) => (
            <StyledFormItem key={field.name} label={field.label} name={field.name}>
              <Select
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                options={CONSENT_OPTIONS}
              />
            </StyledFormItem>
          ))}
        </StyledConsentGrid>
        <StyledNoteArea>
          <StyledFormItem name="isCeaseCommunication" valuePropName="checked">
            <Checkbox>Cease Communication</Checkbox>
          </StyledFormItem>
        </StyledNoteArea>
        <StyledMetaGrid>
          <StyledFormItem label="Affected Accounts" name="accountIds">
            <Select
              showSearch
              optionFilterProp="label"
              mode="multiple"
              allowClear
              popupMatchSelectWidth={false}
              placeholder="Select accounts..."
              options={debtorAccounts?.map((a) => ({
                label: `Account #${a.externalId}`,
                value: a.id,
              }))}
            />
          </StyledFormItem>
          <StyledFormItem
            label="Start Time"
            name="communicationStartTime"
            rules={[{ required: true }]}
          >
            <AktDatePicker
              showSecond={false}
              minuteStep={5}
              showTime
              placeholder="Select Date/Time"
              placement="bottomLeft"
            />
          </StyledFormItem>
        </StyledMetaGrid>
        <StyledNoteArea>
          <StyledFormItem name="notes">
            <Input.TextArea
              ref={textareaRef}
              placeholder="Add a note..."
              autoSize={{ minRows: 2, maxRows: 6 }}
              autoFocus
            />
          </StyledFormItem>
        </StyledNoteArea>
        <StyledActions>
          <StyledHideAdvancedViewButton type="link" size="small" onClick={hideAdvancedView}>
            Hide advanced view
          </StyledHideAdvancedViewButton>
          <Button size="small" onClick={collapse}>
            Cancel
          </Button>
          <Button size="small" type="primary" loading={isLoading} onClick={handleSubmit}>
            Save (⌘↵)
          </Button>
        </StyledActions>
      </Form>
    </StyledComposer>
  );
}

export default InlineComposer;
